import { useMutation } from "@apollo/client";
import { useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual } from "react-redux";
import { selectAllergens, selectAppState, selectOpenedDetailDishTypeId } from "slices/appStateSlice";
import { selectAvailableDishTypesIds, selectFilteredDishTypeIds } from "slices/dishesSlice";
import { selectFridgeLightingMode } from "slices/fridgeLightingSlice";
import { selectActiveLocalTransaction } from "slices/transactionSlice";
import { LightingModeType, TransactionState } from "types";
import { AppState } from "types/mainTypes";
import { getTabletType, TabletTypeEnum } from "utils/browser/localStorage";
import { debugTrace, shouldTraceLightingDebug } from "utils/debug/debugTrace";
import { SWITCH_FRIDGE_MODE_MUTATION, TOGGLE_SPOTLIGHT_MODE_MUTATION } from "utils/gqlOperations";
import { computeDesiredLighting } from "utils/lightingConvergence";
import { createDefaultLight, createSpotlightLight } from "utils/lightingUtils";
import { setShallowEqual } from "utils/setShallowEqual";
import { useFridgeInformation } from "./useFridgeInformation";
import { useAppSelector } from "./utilsHooks/storeHooks";

// How long `appState` must stay IDLE before the lights are allowed to dim to StandBy.
const STANDBY_IDLE_DEBOUNCE_MS = 5000;

// react-idle-timer presence flaps IDLE↔ACTIVE, and dimming to StandBy on every idle blip made the
// fridge lights visibly blink. Gate the IDLE→StandBy transition behind a sustained-idle timer: any
// activity brightens immediately, but the lights only dim once idle has held for
// STANDBY_IDLE_DEBOUNCE_MS. Scoped to the lighting decision so other AppState.IDLE consumers
// (drawer / filter-modal auto-close) keep reacting to idle promptly.
const useDimGatedAppState = (appState: AppState, debounceMs: number): AppState => {
	// Init to the current appState: the debounce applies to active→idle TRANSITIONS (the flap), not
	// to the initial mount. If the app mounts already idle, dimming straight away is correct — it is
	// not a flap.
	const [gatedAppState, setGatedAppState] = useState<AppState>(appState);

	useEffect(() => {
		if (appState !== AppState.IDLE) {
			setGatedAppState(appState); // brighten immediately on any activity
			return;
		}
		const timer = setTimeout(() => setGatedAppState(AppState.IDLE), debounceMs);
		return () => {
			clearTimeout(timer);
		};
	}, [appState, debounceMs]);

	return gatedAppState;
};

// Edge-triggered lighting driver. The FE is the sole authority: it computes a desired lighting target
// from its OWN state and fires a mutation ONCE whenever that intent changes. It does NOT reconcile
// against the BE's echoed `actualMode`/spotlight — reacting to echoes is exactly what let a second
// writer (a stray second master tablet, or the BE/hardware) ping-pong the fridge STAND_BY↔ACTIVE.
// Ignoring echoes makes the war impossible by construction. A dropped mutation is re-delivered by the
// Apollo RetryLink, so we do not need echo-based retries.
//
// The one place the BE still matters: after a transaction finishes it force-StandBys exactly once.
// We watch for that single StandBy and re-assert ACTIVE one time (last-writer-wins) so the fridge
// brightens for the next customer — see the post-order effect below.
//
// `standbyIdleDebounceMs` is injectable so tests can disable the debounce (0 = dim immediately);
// production callers use the default sustained-idle window.
export const useConvergeLighting = (standbyIdleDebounceMs: number = STANDBY_IDLE_DEBOUNCE_MS) => {
	const { id: fridgeId } = useFridgeInformation();
	const [setMode] = useMutation(SWITCH_FRIDGE_MODE_MUTATION);
	const [setSpotlight] = useMutation(TOGGLE_SPOTLIGHT_MODE_MUTATION, {
		// TEMP diagnostic: confirm whether the per-dish spotlight mutation actually reaches/satisfies the
		// BE on the kiosk (the "first dish type lights, later ones don't" symptom). If FIRE-TOGGLE is
		// logged with no TOGGLE-OK (or a TOGGLE-ERR appears), the FE sent it but the transport dropped it.
		onError: (error) => {
			if (shouldTraceLightingDebug()) {
				debugTrace(`[state-debug] TOGGLE-ERR ${error.message}`);
			}
		},
		onCompleted: () => {
			if (shouldTraceLightingDebug()) {
				debugTrace("[state-debug] TOGGLE-OK");
			}
		},
	});

	const appState = useAppSelector(selectAppState);
	const transaction = useAppSelector(selectActiveLocalTransaction);
	const openDetailDishTypeId = useAppSelector(selectOpenedDetailDishTypeId);
	const allergens = useAppSelector(selectAllergens);
	const filteredDishTypeIds = useAppSelector(selectFilteredDishTypeIds);
	const availableDishTypeIds = useAppSelector(selectAvailableDishTypesIds);
	// Read ONLY for the bounded post-order re-assert below — never for the steady-state drive decision.
	const actualMode = useAppSelector(selectFridgeLightingMode);

	const hasTransaction = transaction != null;
	const transactionState = transaction?.state ?? null;

	// Debounced view of appState for the dim decision (see useDimGatedAppState).
	const appStateForLighting = useDimGatedAppState(appState, standbyIdleDebounceMs);

	const desired = useMemo(
		() =>
			computeDesiredLighting({
				appState: appStateForLighting,
				hasTransaction: hasTransaction,
				transactionState: transactionState,
				openDetailDishTypeId: openDetailDishTypeId,
				allergens: allergens,
				filteredDishTypeIds: filteredDishTypeIds,
			}),
		[appStateForLighting, hasTransaction, transactionState, openDetailDishTypeId, allergens, filteredDishTypeIds],
	);

	// What we LAST COMMANDED (our own previous intent) — the edge baseline. Compared against the new
	// desired to decide whether intent actually changed. Note these track what we SENT, not the BE
	// echo, so an echo can never trigger (or suppress) a command.
	const lastCommandedModeRef = useRef<LightingModeType | null>(null);
	const lastCommandedSpotlightRef = useRef<readonly string[] | null>(null);
	// Identity of the available-dish set at the last spotlight send. A change here re-fires the
	// spotlight even with an unchanged target, so a newly-available dish still receives its DEFAULT
	// entry (the payload is built from this set).
	const lastCommandedAvailableRef = useRef<ReadonlySet<string> | null>(null);
	// Armed when a transaction reaches Finished; consumed by the single post-order StandBy re-assert.
	const awaitingPostOrderStandbyRef = useRef(false);
	// Previous transactionState, so the watch is armed only on the EDGE into Finished — re-arming on
	// every render while it stays Finished would let each repeated BE StandBy re-fire Active (a loop).
	const prevTransactionStateRef = useRef<TransactionState | null>(null);

	// Steady-state driver: fire on each change of OUR intent. Ignores the BE echo entirely.
	useEffect(() => {
		if (getTabletType() !== TabletTypeEnum.Master || fridgeId == null) {
			return;
		}

		// MODE edge — fire only when the desired mode differs from what we last commanded.
		if (lastCommandedModeRef.current !== desired.mode) {
			lastCommandedModeRef.current = desired.mode;
			if (shouldTraceLightingDebug()) {
				debugTrace(`[state-debug] FIRE-SWITCH→${desired.mode} (actual=${actualMode})`);
			}
			setMode({ variables: { input: { id: fridgeId, lighting: { mode: desired.mode } } } });
		}

		// SPOTLIGHT edge — per-dish state is only meaningful in Active (a per-dish payload would wake the
		// fridge out of StandBy), and only when there are dishes to address. We KEEP the spotlight ref
		// across a StandBy dip so returning to Active with the same target does not re-fire.
		if (desired.mode !== LightingModeType.Active || availableDishTypeIds.size === 0) {
			return;
		}

		const spotlightChanged =
			lastCommandedSpotlightRef.current == null ||
			!shallowEqual(lastCommandedSpotlightRef.current, desired.spotlightDishTypeIds);
		const availableChanged =
			lastCommandedAvailableRef.current == null ||
			!setShallowEqual(lastCommandedAvailableRef.current, availableDishTypeIds);

		if (!(spotlightChanged || availableChanged)) {
			return;
		}

		lastCommandedSpotlightRef.current = desired.spotlightDishTypeIds;
		lastCommandedAvailableRef.current = availableDishTypeIds;

		const desiredSet = new Set(desired.spotlightDishTypeIds);
		const types = Array.from(availableDishTypeIds).map((id) =>
			desiredSet.has(id) ? createSpotlightLight(id) : createDefaultLight(id),
		);
		if (shouldTraceLightingDebug()) {
			// payloadSpot = how many desired targets actually became SPOTLIGHT entries in the payload (a
			// target only counts if it is in availableDishTypeIds). missing = targets dropped because they
			// are NOT available — the prime suspect for a dish that stays dark.
			const target = desired.spotlightDishTypeIds;
			const payloadSpot = target.filter((id) => availableDishTypeIds.has(id)).length;
			const missing = target.filter((id) => !availableDishTypeIds.has(id));
			debugTrace(
				`[state-debug] FIRE-TOGGLE target=[${target.join(",")}] avail=${availableDishTypeIds.size} payloadSpot=${payloadSpot} missing=[${missing.join(",")}]`,
			);
		}
		setSpotlight({ variables: { input: { id: fridgeId, dishes: { types: types } } } });
	}, [desired, availableDishTypeIds, actualMode, fridgeId, setMode, setSpotlight]);

	// Post-order re-assert: a transaction reaching Finished arms a one-shot watch. The BE force-StandBys
	// once afterwards; when we see that StandBy we re-assert ACTIVE a single time so the FE is the last
	// writer and the fridge brightens for the next customer. Gated on the customer still being present
	// (appStateForLighting === ACTIVE) so someone who has already walked away is NOT yanked back to
	// bright — we let the BE's dim stand and step 5 keeps it dim. One-shot (the ref is consumed) so it
	// can never become a STAND_BY↔ACTIVE loop.
	useEffect(() => {
		const justFinished =
			transactionState === TransactionState.Finished && prevTransactionStateRef.current !== TransactionState.Finished;
		prevTransactionStateRef.current = transactionState ?? null;
		if (justFinished) {
			awaitingPostOrderStandbyRef.current = true;
		}

		if (!awaitingPostOrderStandbyRef.current || actualMode !== LightingModeType.StandBy) {
			return;
		}

		awaitingPostOrderStandbyRef.current = false;

		if (getTabletType() !== TabletTypeEnum.Master || fridgeId == null || appStateForLighting !== AppState.ACTIVE) {
			return;
		}

		lastCommandedModeRef.current = LightingModeType.Active;
		if (shouldTraceLightingDebug()) {
			debugTrace(`[state-debug] FIRE-SWITCH→ACTIVE (post-order re-assert, actual=${actualMode})`);
		}
		setMode({ variables: { input: { id: fridgeId, lighting: { mode: LightingModeType.Active } } } });
	}, [transactionState, actualMode, appStateForLighting, fridgeId, setMode]);
};
